import { Task } from "@/types/v2";

export interface TaskDependencyWarning {
  key: string | null;
  title: string;
  status: string | null;
  reason: string;
}

const FINISHED_STATUSES = new Set(["finished", "completed", "done"]);

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const getStringValue = (
  record: Record<string, unknown>,
  key: string,
): string | null => {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

const normalizeReference = (
  value: unknown,
  reason: string,
): TaskDependencyWarning | null => {
  if (typeof value === "string") {
    const title = value.trim();
    return title ? { key: null, title, status: null, reason } : null;
  }

  if (!isObjectRecord(value)) {
    return null;
  }

  const nestedKeys = ["task", "dependencyTask", "dependsOnTask", "preChallengeTask"];
  for (const nestedKey of nestedKeys) {
    if (nestedKey in value) {
      const nestedReference = normalizeReference(value[nestedKey], reason);
      if (nestedReference) {
        return nestedReference;
      }
    }
  }

  const key = getStringValue(value, "key") ?? getStringValue(value, "taskKey");
  const title =
    getStringValue(value, "title") ??
    getStringValue(value, "additionalTitle") ??
    getStringValue(value, "name") ??
    getStringValue(value, "label") ??
    key;
  const status =
    getStringValue(value, "status") ??
    getStringValue(value, "taskStatus") ??
    getStringValue(value, "state");
  const dependencyReason = getStringValue(value, "reason") ?? reason;

  if (!title) {
    return null;
  }

  return { key, title, status, reason: dependencyReason };
};

const normalizeReferenceList = (
  value: unknown,
  reason: string,
): TaskDependencyWarning[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeReference(item, reason))
      .filter((item): item is TaskDependencyWarning => item !== null);
  }

  const single = normalizeReference(value, reason);
  return single ? [single] : [];
};

const isUnresolved = (warning: TaskDependencyWarning): boolean => {
  if (!warning.status) {
    return true;
  }

  return !FINISHED_STATUSES.has(warning.status.toLowerCase());
};

const uniqueWarnings = (
  warnings: TaskDependencyWarning[],
): TaskDependencyWarning[] => {
  const seen = new Set<string>();

  return warnings.filter((warning) => {
    const key = `${warning.key ?? ""}|${warning.title}|${warning.reason}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const buildTaskDependencyWarnings = (
  task: Task,
  response: Record<string, unknown>,
): TaskDependencyWarning[] => {
  const warnings: TaskDependencyWarning[] = [];

  const responseDependencyFields = [
    "dependencies",
    "unresolvedDependencies",
    "blockedBy",
    "prerequisiteTasks",
    "requiredTasks",
    "dependencyTasks",
    "missingDependencies",
  ];

  responseDependencyFields.forEach((field) => {
    warnings.push(
      ...normalizeReferenceList(
        response[field],
        "Required task must be finished first",
      ),
    );
  });

  const details = task.details as unknown;
  if (isObjectRecord(details)) {
    warnings.push(
      ...normalizeReferenceList(
        details.preChallengeTask,
        "Pre-challenge task must be finished first",
      ),
    );
  }

  return uniqueWarnings(warnings).filter(isUnresolved);
};
